import type { order } from '@cashew/types';
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { BackArrow, Box, PageWithNavigation, Title } from '../../../components';
import { initialOrder } from '../../../helpers/initialorder';
import { Divider } from '../../../library';
import { getFormattedOrderStatus } from '../orders/orders.functions';
import { useGetOrderDetails } from './api';
import css from './index.module.scss';

const { api, dateFormat, priceFormat } = require('@cashew/utils');

const ns = 'order-details';
const KYCRejReason = 'ID_VENDOR_REJECTION';
const textFormatter = (text: string): string =>
	text[0] + text.toLocaleLowerCase().replaceAll('_', ' ').slice(1);

/**
 * Neo Loan Order Detail Page
 */
const NeoLoanOrderDetail = () => {
	const { t } = useTranslation();
	const params = useParams<{ orderId: string; lang: string }>();
	const dispatch = useDispatch();

	const { token, role } = useSelector((state: any) => state.user);

	const [orderDetails, setOrderDetails] = useState<order.OrderType | any>(
		initialOrder,
	);
	const [loading, setLoading] = useState(false);
	const [isOffline, setIsOffline] = useState(false);

	const history = useHistory();

	useEffect(() => {
		if (orderDetails?.productType === 'SUBSCRIPTION') {
			history.push(`/${params.lang}/subscriptions/${params.orderId}`);
		}
	}, [orderDetails]);

	useEffect(() => {
		if (
			orderDetails?.store?.type &&
			orderDetails?.store?.type === 'sharaf_dg'
		) {
			setIsOffline(true);
		}
	}, [orderDetails]);

	useEffect(() => {
		if (orderDetails.id === 0 || !loading) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useGetOrderDetails(
				params.orderId,
				setOrderDetails,
				setLoading,
				dispatch,
				t,
			);
		}
	}, [orderDetails]);

	const productType = useMemo(
		() =>
			orderDetails?.productType === 'SUBSCRIPTION' ? 'subscription' : 'order',
		[orderDetails?.productType],
	);

	const customFields = orderDetails?.customFields || {};
	const customFieldsKeys = Object.keys(customFields);

	return (
		<div className={ns}>
			<Helmet>
				<title>{t('orderDetails.sectionTitle')}</title>
			</Helmet>
			<PageWithNavigation>
				{orderDetails?.productType !== 'SUBSCRIPTION' && (
					<>
						<Title>
							Order {orderDetails.id}
							<BackArrow title="Back to Neo Loan Orders" />
						</Title>
						<Box withHeader className={`${ns}__box`}>
							<div className={`${ns}__line`}>
								<p>
									<span>Status</span>
									<strong>
										{t(
											`status.${getFormattedOrderStatus({
												item: {
													orderStatus: orderDetails.orderStatus,
													shippingStatus: orderDetails.shipping?.status,
													refundStatus: orderDetails.refundStatus,
												},
											})}`,
										)}
									</strong>
								</p>
								<p>
									<span>Total order amount</span>
									<strong>
										{priceFormat({
											currency: orderDetails.currencyCode,
											amount: orderDetails.totalAmount || 0,
										})}
									</strong>
								</p>
								<p>
									<span>Refund status</span>
									<strong>
										{t(
											`status.${
												orderDetails.refundStatus
													? orderDetails.refundStatus
													: 'NOT_REFUNDED'
											}`,
										)}
									</strong>
								</p>
								<p>
									<span>
										{t(
											isOffline
												? 'orderDetails.merchantOrderCode'
												: 'orderDetails.orderReference',
										)}
									</span>
									<strong>{orderDetails.orderReference}</strong>
								</p>
								<p>
									<span>{t('orderDetails.totalRefundAmount')}</span>
									<strong>
										{orderDetails?.purchaseFeeStatus === 'REFUNDED'
											? `${priceFormat({
													currency: orderDetails.currencyCode,
													amount:
														Number(
															(
																(orderDetails.totalAmount || 0) -
																(orderDetails.balanceAmount || 0)
															).toFixed(2),
														) +
														Number(orderDetails?.sumPurchaseFeePurchaseFeeVat),
												})} (Purchase fee included)`
											: priceFormat({
													currency: orderDetails.currencyCode,
													amount: Number(
														(
															(orderDetails.totalAmount || 0) -
															(orderDetails.balanceAmount || 0)
														).toFixed(2),
													),
												})}
									</strong>
								</p>
								<p>
									<span>{t('orderDetails.storeName')}</span>
									<strong>{orderDetails.store?.storeName}</strong>
								</p>

								<p className="order-details-paragrph">
									<span>{t('orderDetails.customerName')}</span>
									<strong>
										{`${orderDetails.customer?.firstName || ''} ${orderDetails.customer?.lastName || ''}`}
									</strong>
								</p>
								<p>
									<span>{t('orderDetails.storeId')}</span>
									<strong>{orderDetails.storeId}</strong>
								</p>

								<p className="order-details-paragrph">
									<span>Customer Email</span>
									<strong>{orderDetails.customer?.email}</strong>
								</p>
								<p>
									<span>{t('orderDetails.orderDate')}</span>
									<strong>
										{orderDetails.id > 0
											? dateFormat({ date: orderDetails.purchaseDate })
											: ''}
									</strong>
								</p>
								<p>
									<span>Customer phone</span>
									<strong>{orderDetails.customer?.mobileNumber}</strong>
								</p>
								<p>
									<span>Rejection reason</span>
									<strong>
										{orderDetails?.rejectionReason
											? textFormatter(orderDetails?.rejectionReason)
											: ''}
									</strong>
								</p>
								<p>
									<span>KYC rejections reason</span>
									<strong>
										{orderDetails?.rejectionReason === KYCRejReason
											? orderDetails?.kycRejectionReason
											: ''}
									</strong>
								</p>

								<p>
									<span>Dispatch date</span>
									<strong>
										{orderDetails?.shipping?.dispatchDate !== null &&
											orderDetails?.shipping?.dispatchDate !== undefined &&
											orderDetails?.shipping?.dispatchDate !== '' &&
											dateFormat({
												date: orderDetails?.shipping?.dispatchDate,
												withTime: true,
											})}
									</strong>
								</p>

								{orderDetails && orderDetails?.sumPurchaseFeePurchaseFeeVat && (
									<p>
										<span>Purchase Fee</span>
										<strong>
											{(orderDetails &&
												`${priceFormat({
													currency: orderDetails?.currencyCode,
													amount: orderDetails?.sumPurchaseFeePurchaseFeeVat,
												})}  (VAT included)`) ||
												0}
										</strong>
									</p>
								)}

								{orderDetails?.additionalFees?.some(
									(fee: any) => fee?.type === 'PROCESSING_FEE',
								) && (
									<p>
										<span>Processing fee</span>
										<strong>
											{(() => {
												const processingFee =
													orderDetails?.additionalFees?.find(
														(fee: any) => fee?.type === 'PROCESSING_FEE',
													);
												return processingFee
													? `${priceFormat({
															currency: orderDetails?.currencyCode,
															amount:
																(processingFee?.feeValue || 0) +
																(processingFee?.feeVat || 0),
														})} (VAT included)`
													: 0;
											})()}
										</strong>
									</p>
								)}
							</div>
						</Box>

						{customFieldsKeys?.length > 0 ? (
							<div className={css.customFields}>
								<h3 className="!text-[18px] font-bold">
									{t('order.customFields')}
								</h3>
								<Divider />
								<Box withHeader className={`${ns}__box ${css.box}`}>
									<div className={css.line}>
										{customFieldsKeys.map((key: any) => (
											<p key={key} className="d-flex justify-content-between">
												<span className={css.field}>{key}</span>
												<span className={css.field}>{customFields[key]}</span>
											</p>
										))}
									</div>
								</Box>
							</div>
						) : null}
					</>
				)}
			</PageWithNavigation>
		</div>
	);
};

export default NeoLoanOrderDetail;
